import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, fmtDate, formatApiErrorDetail, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, ArrowLeft, FlaskConical, ScanLine } from "lucide-react";
import { toast } from "sonner";
import { QrScanDialog } from "@/components/QrScanDialog";
import { ProductLabel } from "@/components/ProductLabel";

export default function Production() {
  const { id } = useParams();
  if (id) return <ProductionDetail id={id} />;
  return <ProductionList />;
}

function ProductionList() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const blank = () => ({ merek: "", batch: "", production_date: new Date().toISOString().slice(0, 10), petugas: user?.name || "" });
  const [f, setF] = useState(blank);

  function load() { api.get("/production-batches").then((r) => setRows(r.data)); }
  useEffect(() => { load(); }, []);

  async function save() {
    try { await api.post("/production-batches", f); toast.success("Batch produksi dibuat."); setOpen(false); setF(blank()); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold">MJ-FORM-23 — Formulir Pencatatan Proses</h1>
          <p className="text-sm text-muted-foreground">Buat batch produksi, ambil bahan dengan scan label, sahkan untuk mengurangi Kartu Stok MJ-FORM-02, lalu cetak label produk.</p></div>
        {["gudang", "manajer", "admin"].includes(user?.role) && <Button data-testid="new-prod-btn" onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-1" /> Batch Baru</Button>}
      </div>
      <Card className="p-4 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b">
            <th className="py-2 font-medium">Kode Produksi</th><th className="font-medium">Merek</th><th className="font-medium">Batch</th><th className="font-medium">Hasil Produksi</th><th className="font-medium">Tanggal</th><th className="font-medium">Status</th>
          </tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid={`prod-row-${p.code}`} className="border-b border-slate-100 hover:bg-accent/50 cursor-pointer" onClick={() => nav(`/production/${p.id}`)}>
                <td className="py-2"><span className="batch-chip">{p.tracking_no || p.code}</span></td><td>{p.merek || p.product_name}</td><td>{p.batch || p.batch_seq}</td><td>{p.jumlah_produk_jadi || "-"}</td><td>{fmtDate(p.production_date)}</td>
                <td className="text-xs font-semibold">{p.finalized ? <span className="text-emerald-700">SELESAI PRODUKSI</span> : <span className="text-amber-700">PROSES</span>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-muted-foreground">Belum ada batch produksi.</td></tr>}
          </tbody>
        </table>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Batch Produksi Baru</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Merek</Label><Input data-testid="prod-merek" value={f.merek} onChange={(e) => setF({ ...f, merek: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Batch</Label><Input data-testid="prod-batch" value={f.batch} onChange={(e) => setF({ ...f, batch: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Tanggal</Label><Input data-testid="prod-date" type="date" value={f.production_date} onChange={(e) => setF({ ...f, production_date: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Petugas</Label><Input data-testid="prod-petugas" value={f.petugas} onChange={(e) => setF({ ...f, petugas: e.target.value })} className="mt-1" /></div>
            <p className="text-xs text-muted-foreground">Hasil produksi diisi setelah proses selesai, ditimbang, dan dilaporkan di halaman pencatatan proses.</p>
          </div>
          <DialogFooter><Button data-testid="prod-save" onClick={save}>Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ProductionDetail({ id }) {
  const nav = useNavigate();
  const { user } = useAuth();
  const [pb, setPb] = useState(null);

  async function load() {
    const { data } = await api.get(`/production-batches/${id}`); setPb(data);
  }
  useEffect(() => { load(); }, [id]);

  if (!pb) return <div className="text-muted-foreground">Memuat...</div>;

  return (
    <div className="space-y-5">
      <button onClick={() => nav("/production")} className="text-sm text-muted-foreground flex items-center gap-1 hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Kembali</button>
      <div className="flex items-center gap-3"><FlaskConical className="w-6 h-6 text-primary" /><div>
        <span className="batch-chip text-sm">{pb.code}</span>
        {pb.tracking_no && <span className="batch-chip text-sm ml-2" data-testid="prod-tracking">#{pb.tracking_no}</span>}
        <h1 className="font-heading text-2xl font-extrabold">{pb.merek || pb.product_name}</h1>
        <p className="text-sm text-muted-foreground">Batch {pb.batch || pb.batch_seq} • {fmtDate(pb.production_date)}{pb.petugas ? ` • Petugas ${pb.petugas}` : ""}{pb.jumlah_produk_jadi ? ` • Hasil ${pb.jumlah_produk_jadi}` : ""}</p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {pb.finalized && <span className="text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700 font-semibold">SELESAI PRODUKSI</span>}
        <Button variant="outline" size="sm" data-testid="dl-mjform23" onClick={() => downloadFile(`/reports/mj-form-23.pdf?production_id=${pb.id}`, `MJ-FORM-23_${pb.tracking_no || pb.code}.pdf`)}>MJ-FORM-23 PDF</Button>
      </div></div>

      <ProcessRecord pb={pb} reload={load} />
      {pb.finalized && <ProductLabel pb={pb} />}

      <Card className="p-4">
        <h4 className="font-heading font-bold text-sm mb-2">Bahan yang Digunakan (Hulu)</h4>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b"><th className="py-2 font-medium">Batch Bahan</th><th className="font-medium">Bahan</th><th className="font-medium">Jumlah Dipakai</th><th className="font-medium">Waktu</th></tr></thead>
          <tbody>
            {(pb.consumption || []).map((m) => (
              <tr key={m.id} className="border-b border-slate-100 cursor-pointer hover:bg-accent/50" onClick={() => nav(`/traceability?batch=${m.internal_batch}`)}>
                <td className="py-2"><span className="batch-chip">{m.internal_batch}</span></td><td>{m.material_name}</td><td>{m.qty_out} {m.unit}</td><td className="text-xs">{m.created_at?.slice(0,10)}</td>
              </tr>
            ))}
            {(pb.consumption || []).length === 0 && <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">Belum ada bahan dikeluarkan.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function LotPicker({ ing, i, disabled, onPick }) {
  const [code, setCode] = useState("");
  const [scan, setScan] = useState(false);
  async function lookup(c) {
    if (!c?.trim()) return;
    try {
      const { data } = await api.get("/stock/lookup", { params: { code: c } });
      onPick(data); setCode("");
      toast.success(`${data.material_name} — batch ${data.internal_batch} (sisa ${data.qty_remaining} ${data.unit})`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  }
  if (ing.lot_id) return (
    <div className="text-xs border rounded px-2 py-1.5 bg-emerald-50 border-emerald-200" data-testid={`ing-lot-${i}`}>
      <b>{ing.material_name || ing.label}</b> • <span className="font-mono">{ing.tracking_no || ing.internal_batch}</span>
      {ing.qty_remaining != null && <span className="text-muted-foreground"> • sisa {ing.qty_remaining} {ing.unit}</span>}
      {!disabled && <button className="ml-2 text-red-600 underline" onClick={() => onPick(null)} data-testid={`ing-lot-clear-${i}`}>ganti</button>}
    </div>
  );
  if (disabled) return <span className="text-xs text-muted-foreground">tanpa stok</span>;
  return (
    <div className="flex gap-1">
      <Input className="w-44" placeholder="Scan / ketik no. tracking" value={code} onChange={(e) => setCode(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") lookup(code); }} data-testid={`ing-code-${i}`} />
      <Button size="sm" variant="outline" onClick={() => lookup(code)} data-testid={`ing-lookup-${i}`}>Cek</Button>
      <Button size="sm" variant="outline" onClick={() => setScan(true)} data-testid={`ing-scan-${i}`}><ScanLine className="w-4 h-4" /></Button>
      <QrScanDialog open={scan} onClose={() => setScan(false)} onResult={lookup} />
    </div>
  );
}

const nowHM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };

function TimeStamp({ label, value, disabled, onSet, testid }) {
  if (value) return (
    <span className="text-sm font-mono font-semibold px-2 py-1 rounded bg-emerald-50 border border-emerald-200" data-testid={`${testid}-value`}>
      {label}: {value}
      {!disabled && <button className="ml-2 text-xs text-red-600 underline font-sans" onClick={() => onSet("")} data-testid={`${testid}-reset`}>ulang</button>}
    </span>
  );
  if (disabled) return <span className="text-xs text-muted-foreground">{label}: -</span>;
  return <Button size="sm" variant="outline" onClick={() => onSet(nowHM())} data-testid={testid}>{label}</Button>;
}

const DEFAULT_STAGES = ["Persiapan / Bahan Masak", "Proses Homogen", "Proses Vakum + Pasteurisasi"];
const LEGACY = [["persiapan", DEFAULT_STAGES[0]], ["homogen", DEFAULT_STAGES[1]], ["vakum", DEFAULT_STAGES[2]]];
const stagesFrom = (proc) => {
  if (proc?.stages) return proc.stages;
  const old = LEGACY.filter(([k]) => proc?.[k]).map(([k, name]) => ({ name, ...proc[k] }));
  return old.length ? old : DEFAULT_STAGES.map((name) => ({ name, mulai: "", selesai: "" }));
};

const DEFAULT_ING = ["JAHE", "MADU", "LEMON", "APEL", "BAWANG PUTIH", "AIR"];

function ProcessRecord({ pb, reload }) {
  const { user } = useAuth();
  const finalized = pb.finalized;
  const [ings, setIngs] = useState([]);
  const [stages, setStages] = useState([]);
  const [jumlah, setJumlah] = useState("");
  const [paraf, setParaf] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (pb.ingredients && pb.ingredients.length) setIngs(pb.ingredients);
    else setIngs(DEFAULT_ING.map((l) => ({ label: l, material_id: "", qty: 0, unit: "kg" })));
    setStages(stagesFrom(pb.process));
    setJumlah(pb.jumlah_produk_jadi || ""); setParaf(pb.paraf || "");
  }, [pb.id]);

  const canEdit = ["gudang", "qc", "manajer", "admin"].includes(user?.role) && !finalized;
  const setIng = (i, k, v) => setIngs((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const pickLot = (i, d) => setIngs((p) => p.map((x, idx) => (idx !== i ? x : d
    ? { ...x, lot_id: d.lot_id, internal_batch: d.internal_batch, tracking_no: d.tracking_no, material_id: d.material_id, material_name: d.material_name, qty_remaining: d.qty_remaining, unit: d.unit, label: d.material_name }
    : { ...x, lot_id: "", internal_batch: "", tracking_no: "", material_id: "", material_name: "", qty_remaining: null })));

  async function save(finalize) {
    setBusy(true);
    try {
      await api.post(`/production-batches/${pb.id}/process`, {
        ingredients: ings.map((x) => ({ ...x, qty: parseFloat(x.qty) || 0 })),
        stages, jumlah_produk_jadi: jumlah, paraf, finalize,
      });
      toast.success(finalize ? "Produksi selesai & stok Kartu Stok MJ-FORM-02 dikurangi." : "Pencatatan proses tersimpan.");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  const setSt = (i, k, v) => setStages((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  return (
    <Card className="p-4" data-testid="process-record">
      <h4 className="font-heading font-bold text-sm mb-3">Pencatatan Proses (MJ-FORM-23){finalized && <span className="ml-2 text-xs text-emerald-600">— DISAHKAN</span>}</h4>
      <div className="mb-3">
        <div className="text-xs font-semibold mb-1">Berat Bahan yang Digunakan — wajib scan label / no. tracking bahan; stok berkurang saat produksi disahkan selesai. Bahan tanpa stok (mis. AIR) cukup diisi nama &amp; berat.</div>
        <div className="space-y-2">
          {ings.map((ing, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <Input className="w-32" placeholder="Nama (label)" disabled={!canEdit} value={ing.label} onChange={(e) => setIng(i, "label", e.target.value)} data-testid={`ing-label-${i}`} />
              <LotPicker ing={ing} i={i} disabled={!canEdit} onPick={(d) => pickLot(i, d)} />
              <Input className="w-24" type="number" step="any" placeholder="qty" disabled={!canEdit} value={ing.qty} onChange={(e) => setIng(i, "qty", e.target.value)} data-testid={`ing-qty-${i}`} />
              <Input className="w-20" placeholder="satuan" disabled={!canEdit} value={ing.unit} onChange={(e) => setIng(i, "unit", e.target.value)} data-testid={`ing-unit-${i}`} />
              {canEdit && <Button size="sm" variant="ghost" onClick={() => setIngs((p) => p.filter((_, idx) => idx !== i))}>✕</Button>}
            </div>
          ))}
        </div>
        {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => setIngs((p) => [...p, { label: "", material_id: "", qty: 0, unit: "kg" }])} data-testid="ing-add">+ Bahan</Button>}
      </div>
      <div className="mb-3">
        <div className="text-xs font-semibold mb-1">Tahapan Proses (nama proses bebas diisi)</div>
        <div className="space-y-2">
          {stages.map((st, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`stage-row-${i}`}>
              <Input className="w-64" placeholder="Nama proses" disabled={!canEdit} value={st.name} onChange={(e) => setSt(i, "name", e.target.value)} data-testid={`stage-name-${i}`} />
              <TimeStamp label="Mulai" value={st.mulai} disabled={!canEdit} onSet={(v) => setSt(i, "mulai", v)} testid={`stage-mulai-${i}`} />
              <TimeStamp label="Selesai" value={st.selesai} disabled={!canEdit || !st.mulai} onSet={(v) => setSt(i, "selesai", v)} testid={`stage-selesai-${i}`} />
              {canEdit && <Button size="sm" variant="ghost" onClick={() => setStages((p) => p.filter((_, idx) => idx !== i))} data-testid={`stage-del-${i}`}>✕</Button>}
            </div>
          ))}
        </div>
        {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => setStages((p) => [...p, { name: "", mulai: "", selesai: "" }])} data-testid="stage-add">+ Proses</Button>}
      </div>
      <div className="grid sm:grid-cols-2 gap-2 mb-3">
        <div><Label className="text-xs">Hasil Produksi (setelah ditimbang)</Label><Input disabled={!canEdit} value={jumlah} onChange={(e) => setJumlah(e.target.value)} placeholder="mis. 20 kilogram" data-testid="proc-jumlah" className="mt-1" /></div>
        <div><Label className="text-xs">Paraf</Label><Input disabled={!canEdit} value={paraf} onChange={(e) => setParaf(e.target.value)} placeholder="mis. SYAMSUL" data-testid="proc-paraf" className="mt-1" /></div>
      </div>
      {canEdit && (
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy} onClick={() => save(false)} data-testid="proc-save">Simpan Draft</Button>
          <Button disabled={busy} onClick={() => save(true)} data-testid="proc-finalize">Sahkan Selesai &amp; Kurangi Stok</Button>
        </div>
      )}
      {finalized && <p className="text-xs text-muted-foreground mt-2">Disahkan oleh {pb.finalized_by} — tidak dapat diubah.</p>}
    </Card>
  );
}
